import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, Phone, Car, Calendar, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const VehicleDetailPage = () => {
  const { vehicleId } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showBookingModal, setShowBookingModal] = useState(false);

  useEffect(() => {
    fetchVehicle();
  }, [vehicleId]);

  const fetchVehicle = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/${vehicleId}`);
      setVehicle(response.data);
    } catch (error) {
      console.error("Error fetching vehicle:", error);
      toast.error("Vehicle not found");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, currency = "JMD", status = "available") => {
    if (status === "special_order") {
      return "Special Order";
    }
    if (!price || price === 0) {
      return "Contact for Price";
    }
    if (currency === "JMD") {
      if (price >= 1000000) {
        const millions = price / 1000000;
        return `JMD $${millions.toFixed(3)}m`;
      }
      return `JMD $${price.toLocaleString()}`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const nextImage = () => {
    if (vehicle?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % vehicle.images.length);
    }
  };

  const prevImage = () => {
    if (vehicle?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + vehicle.images.length) % vehicle.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
        <div className="w-12 h-12 spinner"></div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-4">Vehicle Not Found</h1>
          <Link to="/cars" className="btn-primary">
            Back to Cars
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/logo.png" 
                alt="Trish Drives Honda" 
                className="h-10"
              />
            </Link>
            <nav className="flex items-center gap-6">
              <Link to="/cars" className="text-[#4A4A4A] hover:text-[#E91E8C] transition-colors">
                All Vehicles
              </Link>
              <Link to="/login" className="btn-secondary text-sm px-4 py-2">
                Login
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <Link 
          to="/cars" 
          className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#E91E8C] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to All Vehicles
        </Link>
      </div>

      {/* Vehicle Content */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-white shadow-lg">
              <img
                src={vehicle.images[currentImageIndex]}
                alt={vehicle.name}
                className="w-full h-full object-cover"
              />
              {vehicle.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-[#1A1A1A]" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-[#1A1A1A]" />
                  </button>
                </>
              )}
              {vehicle.status === "special_order" && (
                <div className="absolute top-4 right-4 bg-purple-600 text-white text-sm font-medium px-4 py-2 rounded-full">
                  Special Order
                </div>
              )}
              {vehicle.status === "coming_soon" && (
                <div className="absolute top-4 right-4 bg-[#E91E8C] text-white text-sm font-medium px-4 py-2 rounded-full">
                  Coming Soon
                </div>
              )}
            </div>
            
            {/* Thumbnail Strip */}
            {vehicle.images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {vehicle.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      idx === currentImageIndex ? 'border-[#E91E8C]' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt={`${vehicle.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Vehicle Info */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm bg-[#FFF5F9] text-[#E91E8C] px-3 py-1 rounded-full font-medium">
                  {vehicle.body_type}
                </span>
                <span className="text-sm text-[#6B5E55]">{vehicle.year}</span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4">
                {vehicle.name}
              </h1>
              <p className="text-3xl font-bold text-[#E91E8C]">
                {formatPrice(vehicle.price, vehicle.currency, vehicle.status)}
              </p>
            </div>

            {vehicle.description && (
              <p className="text-[#4A4A4A] leading-relaxed">
                {vehicle.description}
              </p>
            )}

            {/* Features */}
            {vehicle.features && vehicle.features.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-4">
                  Features & Specifications
                </h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {vehicle.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-[#4A4A4A]">
                      <Check className="w-5 h-5 text-[#E91E8C] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="space-y-4 pt-4">
              <Button 
                onClick={() => setShowBookingModal(true)}
                className="w-full btn-primary text-lg py-6"
                disabled={vehicle.status === "sold"}
              >
                {vehicle.status === "special_order" ? "Inquire Now" : "Book a Visit / Test Drive"}
              </Button>
              
              <div className="flex items-center justify-center gap-6 text-sm text-[#6B5E55]">
                <a href="tel:+18765644901" className="flex items-center gap-2 hover:text-[#E91E8C] transition-colors">
                  <Phone className="w-4 h-4" />
                  1876-564-4901
                </a>
              </div>
            </div>

            {/* Location Note */}
            <div className="flex items-center gap-2 bg-[#FFF5F9] px-4 py-3 rounded-lg border border-[#E91E8C]/20 text-sm text-[#4A4A4A]">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#E91E8C] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Showroom visits & test drives at <strong>ATL Hagley Park, Kingston</strong></span>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={showBookingModal} 
        onClose={() => setShowBookingModal(false)} 
        vehicle={vehicle}
      />

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-400">© 2025 Trish Drives Honda. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

// Multi-step Booking Modal
const BookingModal = ({ isOpen, onClose, vehicle }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    vehicle_interested: vehicle?.name || "",
    lead_source: "",
    lead_source_other: "",
    notes: "",
    booking_type: "",
    preferred_date: "",
    preferred_time: ""
  });

  useEffect(() => {
    if (vehicle) {
      setFormData(prev => ({ ...prev, vehicle_interested: vehicle.name }));
    }
  }, [vehicle]);

  const handleClose = () => {
    setStep(1);
    setSubmitted(false);
    setFormData({
      full_name: "",
      email: "",
      phone: "",
      vehicle_interested: vehicle?.name || "",
      lead_source: "",
      lead_source_other: "",
      notes: "",
      booking_type: "",
      preferred_date: "",
      preferred_time: ""
    });
    onClose();
  };

  const handleNext = () => {
    if (!formData.full_name || !formData.email || !formData.phone || !formData.lead_source) {
      toast.error("Please fill in all required fields");
      return;
    }
    setStep(2);
  };

  const handleBookingTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, booking_type: type }));
    if (type === "consultation_call") {
      handleSubmit(type);
    } else {
      setStep(3);
    }
  };

  const handleSubmit = async (bookingType = formData.booking_type) => {
    if ((bookingType === "showroom_visit" || bookingType === "test_drive") && 
        (!formData.preferred_date || !formData.preferred_time)) {
      toast.error("Please select a date and time");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        booking_type: bookingType
      });
      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting booking:", error);
      toast.error("Failed to submit booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Generate available dates (weekdays only, next 30 days)
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const day = date.getDay();
      if (day !== 0 && day !== 6) { // Exclude weekends
        dates.push(date.toISOString().split('T')[0]);
      }
    }
    return dates;
  };

  // Generate time slots (9am - 4pm)
  const getTimeSlots = () => {
    return [
      "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
      "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
      "3:00 PM", "3:30 PM", "4:00 PM"
    ];
  };

  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-white" data-testid="booking-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#2C2420]">
            {submitted ? "Thank You!" : 
             step === 1 ? "Book Your Visit" : 
             step === 2 ? "Select Booking Type" : 
             "Select Date & Time"}
          </DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-8">
            <div className="w-20 h-20 rounded-full bg-[#388E3C]/10 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-[#388E3C]" />
            </div>
            <p className="text-lg text-[#6B5E55] mb-2">
              Your booking request has been submitted!
            </p>
            <p className="text-sm text-[#6B5E55] mb-8">
              We'll contact you within 24 hours to confirm your {formData.booking_type?.replace('_', ' ')}.
            </p>
            <Button onClick={handleClose} className="btn-secondary" data-testid="close-modal-btn">
              Close
            </Button>
          </div>
        ) : step === 1 ? (
          /* Step 1: Contact Info */
          <div className="space-y-4">
            {/* Location Note */}
            <div className="flex items-center gap-2 bg-[#FFF5F9] px-4 py-3 rounded-lg border border-[#E91E8C]/20 text-sm text-[#4A4A4A]">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-[#E91E8C] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Showroom visits & test drives at <strong>ATL Hagley Park, Kingston</strong></span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <Label className="form-label">Full Name *</Label>
                <Input
                  type="text"
                  placeholder="John Smith"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="form-input"
                  required
                  data-testid="input-name"
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Email *</Label>
                <Input
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input"
                  required
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="form-group">
                <Label className="form-label">Phone Number *</Label>
                <Input
                  type="tel"
                  placeholder="+1 876 XXX XXXX"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="form-input"
                  required
                  data-testid="input-phone"
                />
              </div>
              <div className="form-group">
                <Label className="form-label">Vehicle Interested In *</Label>
                <Input
                  type="text"
                  value={formData.vehicle_interested}
                  onChange={(e) => setFormData({ ...formData, vehicle_interested: e.target.value })}
                  className="form-input"
                  readOnly={!!vehicle}
                  data-testid="input-vehicle"
                />
              </div>
            </div>

            <div className="form-group">
              <Label className="form-label">How Did You Find Us? *</Label>
              <Select 
                value={formData.lead_source} 
                onValueChange={(v) => setFormData({ ...formData, lead_source: v })}
              >
                <SelectTrigger className="form-input" data-testid="select-source">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Google Search">Google Search</SelectItem>
                  <SelectItem value="Facebook">Facebook</SelectItem>
                  <SelectItem value="Instagram">Instagram</SelectItem>
                  <SelectItem value="TikTok">TikTok</SelectItem>
                  <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                  <SelectItem value="Referral">Friend/Family Referral</SelectItem>
                  <SelectItem value="Returning Customer">Returning Customer</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.lead_source === "Other" && (
              <div className="form-group">
                <Label className="form-label">Please specify</Label>
                <Input
                  type="text"
                  placeholder="How did you hear about us?"
                  value={formData.lead_source_other}
                  onChange={(e) => setFormData({ ...formData, lead_source_other: e.target.value })}
                  className="form-input"
                />
              </div>
            )}

            <div className="form-group">
              <Label className="form-label">Additional Notes</Label>
              <Textarea
                placeholder="Any specific questions or requirements?"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="form-input min-h-[80px]"
                data-testid="input-notes"
              />
            </div>

            <Button 
              onClick={handleNext}
              className="w-full btn-primary"
              data-testid="next-btn"
            >
              Next
            </Button>
          </div>
        ) : step === 2 ? (
          /* Step 2: Booking Type Selection */
          <div className="space-y-4 py-4">
            <p className="text-center text-[#4A4A4A] mb-6">
              How would you like to proceed?
            </p>
            
            <button
              onClick={() => handleBookingTypeSelect("consultation_call")}
              className="w-full p-6 rounded-xl border-2 border-[#E5E7EB] hover:border-[#E91E8C] hover:bg-[#FFF5F9] transition-all text-left group"
              data-testid="btn-consultation"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#E91E8C]/10 flex items-center justify-center group-hover:bg-[#E91E8C]/20">
                  <Phone className="w-6 h-6 text-[#E91E8C]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#1A1A1A]">Book a Consultation Call</h3>
                  <p className="text-sm text-[#6B5E55]">We'll call you to discuss your options</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleBookingTypeSelect("showroom_visit")}
              className="w-full p-6 rounded-xl border-2 border-[#E5E7EB] hover:border-[#E91E8C] hover:bg-[#FFF5F9] transition-all text-left group"
              data-testid="btn-showroom"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#E91E8C]/10 flex items-center justify-center group-hover:bg-[#E91E8C]/20">
                  <Car className="w-6 h-6 text-[#E91E8C]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#1A1A1A]">Book a Showroom Visit</h3>
                  <p className="text-sm text-[#6B5E55]">Visit us at ATL Hagley Park, Kingston</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleBookingTypeSelect("test_drive")}
              className="w-full p-6 rounded-xl border-2 border-[#E5E7EB] hover:border-[#E91E8C] hover:bg-[#FFF5F9] transition-all text-left group"
              data-testid="btn-testdrive"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#E91E8C]/10 flex items-center justify-center group-hover:bg-[#E91E8C]/20">
                  <Calendar className="w-6 h-6 text-[#E91E8C]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-[#1A1A1A]">Book a Test Drive</h3>
                  <p className="text-sm text-[#6B5E55]">Experience the vehicle firsthand</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setStep(1)}
              className="w-full text-center text-[#6B5E55] hover:text-[#E91E8C] transition-colors text-sm mt-4"
            >
              ← Back to details
            </button>
          </div>
        ) : (
          /* Step 3: Date & Time Selection */
          <div className="space-y-6 py-4">
            <div className="text-center mb-4">
              <span className="inline-flex items-center gap-2 bg-[#FFF5F9] px-4 py-2 rounded-full text-[#E91E8C] font-medium">
                {formData.booking_type === "showroom_visit" ? (
                  <><Car className="w-4 h-4" /> Showroom Visit</>
                ) : (
                  <><Calendar className="w-4 h-4" /> Test Drive</>
                )}
              </span>
            </div>

            <div className="form-group">
              <Label className="form-label">Select Date *</Label>
              <Select 
                value={formData.preferred_date} 
                onValueChange={(v) => setFormData({ ...formData, preferred_date: v })}
              >
                <SelectTrigger className="form-input" data-testid="select-date">
                  <SelectValue placeholder="Choose a weekday" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableDates().map((date) => (
                    <SelectItem key={date} value={date}>
                      {formatDateDisplay(date)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#6B5E55] mt-1">Available Monday - Friday only</p>
            </div>

            <div className="form-group">
              <Label className="form-label">Select Time *</Label>
              <Select 
                value={formData.preferred_time} 
                onValueChange={(v) => setFormData({ ...formData, preferred_time: v })}
              >
                <SelectTrigger className="form-input" data-testid="select-time">
                  <SelectValue placeholder="Choose a time slot" />
                </SelectTrigger>
                <SelectContent>
                  {getTimeSlots().map((time) => (
                    <SelectItem key={time} value={time}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#6B5E55] mt-1">Available 9:00 AM - 4:00 PM</p>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={() => setStep(2)}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
              <Button 
                onClick={() => handleSubmit()}
                className="flex-1 btn-primary"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? "Submitting..." : "Confirm Booking"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VehicleDetailPage;
